import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Button, Alert, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, FormControl, InputLabel, Select, OutlinedInput,
  Grid, InputAdornment, TablePagination, Divider, Switch,
  FormControlLabel, Avatar, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add, Edit, Delete, Home, People, WarningAmber, Visibility,
  VisibilityOff, Search, AutoFixHigh, MarkEmailRead,
  AdminPanelSettings, FamilyRestroom, Badge, CheckCircle, Cancel, School,
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const ROLES    = [
  { value: 'admin',       label: 'Administrator', icon: AdminPanelSettings, color: '#c62828' },
  { value: 'user',        label: 'Staff User',    icon: Badge,             color: '#1565c0' },
  { value: 'classTeacher', label: 'Class Teacher', icon: School,            color: '#00838f' },
  { value: 'parent',      label: 'Parent',        icon: FamilyRestroom,    color: '#00695c' },
];
const STATUSES = ['Active', 'Inactive', 'Blocked'];

const STATUS_COLOR = { Active: 'success', Inactive: 'default', Blocked: 'error' };
const ROLE_COLOR   = { admin: 'error', user: 'primary', classTeacher: 'info', parent: 'success' };

const genPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let p = '';
  for (let i = 0; i < 9; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p + '@' + Math.floor(10 + Math.random() * 89); // e.g. "Km7pQr2xt@42"
};

const emptyForm = {
  email: '', username: '', password: '', fullName: '', role: 'user',
  status: 'Active', userGroup: '', permissionSets: [], employeeNo: '', admNo: '',
  mustChangePassword: true, schoolId: '', isSuperAdmin: false,
};

const UserList = () => {
  const navigate = useNavigate();
  const viewer = JSON.parse(localStorage.getItem('user') || '{}');
  const viewerIsSuperAdmin = !!viewer.isSuperAdmin;
  const [users,     setUsers]     = useState([]);
  const [groups,    setGroups]    = useState([]);
  const [pSets,     setPSets]     = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [search,    setSearch]    = useState('');
  const [dlgOpen,   setDlgOpen]   = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dlgOpen, value: form, onClose: () => { setDlgOpen(false); setError(''); } });
  const [saving,    setSaving]    = useState(false);
  const [confirmDlg,setConfirmDlg]= useState(null);
  const [showPwd,   setShowPwd]   = useState(false);
  const [manualPwd, setManualPwd] = useState(false);   // edit mode: reveal manual override
  const [resetSending, setResetSending] = useState(false);
  const [page,      setPage]      = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [schools,   setSchools]   = useState([]);

  const isSuperAdmin = viewerIsSuperAdmin;

  const load = useCallback(async () => {
    try {
      const [uRes, gRes, psRes, eRes] = await Promise.all([
        axios.get(`${API}/api/users`,           { headers: hdrs() }),
        axios.get(`${API}/api/user-groups`,     { headers: hdrs() }),
        axios.get(`${API}/api/permission-sets`, { headers: hdrs() }),
        axios.get(`${API}/api/employees`,       { headers: hdrs() }).catch(() => ({ data: [] })),
      ]);
      setUsers(uRes.data);
      setGroups(gRes.data);
      setPSets(psRes.data);
      setEmployees(eRes.data || []);
      // Schools registry is super-admin only — used for reassigning users
      if (isSuperAdmin) {
        try {
          const sRes = await axios.get(`${API}/api/schools`, { headers: hdrs() });
          setSchools(sRes.data);
        } catch { /* not fatal */ }
      }
    } catch { setError('Failed to load users'); }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, password: genPassword() });
    setShowPwd(true); setManualPwd(false); setError(''); setDlgOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email:              u.email || '',
      username:           u.username || '',
      password:           '',
      fullName:           u.fullName || '',
      role:               u.role || 'user',
      status:             u.status || 'Active',
      userGroup:          u.userGroup || '',
      permissionSets:     u.permissionSets || [],
      employeeNo:         u.employeeNo || '',
      admNo:              u.admNo || '',
      mustChangePassword: !!u.mustChangePassword,
      schoolId:           u.schoolId || '',
      isSuperAdmin:       !!u.isSuperAdmin,
    });
    setShowPwd(false); setManualPwd(false); setError(''); setDlgOpen(true);
  };

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  // Role-driven field visibility
  const role = form.role;
  const showAccess  = true;                       // group + permission sets shown for all roles
  const showEmpLink = ['user', 'classTeacher'].includes(role);  // employee number for staff and teachers
  const showAdmLink = role === 'parent';          // admission number

  const handleSave = async () => {
    if (!form.email) { setError('Email is required'); return; }
    if (!editing && !form.password) { setError('A temporary password is required for new users'); return; }
    if (form.password && form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!isSuperAdmin) delete payload.schoolId;   // only super-admin reassigns schools
      // Strip access fields that don't apply to the chosen role
      if (!showAccess)  { payload.userGroup = ''; payload.permissionSets = []; }
      if (!showEmpLink) payload.employeeNo = '';
      if (!showAdmLink) payload.admNo = '';
      if (!viewerIsSuperAdmin) delete payload.isSuperAdmin; // backend also enforces this

      if (editing) {
        await axios.put(`${API}/api/users/${editing._id}`, payload, { headers: hdrs() });
        // If admin just changed their own role, keep localStorage in sync
        // so the Dashboard header reflects the new role without a re-login.
        const me = JSON.parse(localStorage.getItem('user') || '{}');
        if (me.id === editing._id || me._id === editing._id) {
          localStorage.setItem('user', JSON.stringify({ ...me, role: payload.role }));
        }
      } else {
        await axios.post(`${API}/api/users`, payload, { headers: hdrs() });
      }
      setDlgOpen(false);
      await load();   // wait for the list to refresh before showing success
      setSuccess(editing ? 'User updated' : 'User created — share the temporary password securely');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const sendResetEmail = async () => {
    if (!editing?.email) return;
    setResetSending(true); setError('');
    try {
      const res = await axios.post(`${API}/api/auth/forgot-password`, { email: editing.email });
      if (res.data.emailFailed) {
        setError('Email delivery unavailable. Use the manual password override below to set a temporary password for this user.');
      } else {
        setSuccess(`Password reset code emailed to ${editing.email}`);
        setDlgOpen(false);
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send reset email');
    } finally { setResetSending(false); }
  };

  const handleDelete = async () => {
    const u = confirmDlg; setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/users/${u._id}`, { headers: hdrs() });
      load(); setSuccess('User deleted'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.fullName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q));
  }, [users, search]);

  const paged = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const roleMeta = (r) => ROLES.find(x => x.value === r) || ROLES[1];

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  return (
    <Box sx={{ maxWidth: 1150, mx: 'auto', mt: { xs: 1.5, sm: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <People color="primary" />
              <Typography variant="h5" color="primary">Users</Typography>
              <Chip label={users.length} size="small" color="primary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <TextField
                size="small" placeholder="Search users…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ width: { xs: '100%', sm: 220 } }}
              />
              <Tooltip title="Back to Setups">
                <IconButton onClick={() => navigate('/setups')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ flex: { xs: 1, sm: 'none' } }}>
                New User
              </Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {/* While the edit dialog is open, errors show inside it — not behind it */}
          {error && !dlgOpen && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {isMobile ? (
            /* ── Phone: card list ── */
            <Box display="flex" flexDirection="column" gap={1.2}>
              {paged.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" py={3}>
                  {search ? 'No users match your search.' : 'No users yet.'}
                </Typography>
              ) : paged.map(u => {
                const rm = roleMeta(u.role);
                return (
                  <Card key={u._id} variant="outlined" onClick={() => openEdit(u)}
                    sx={{ borderRadius: 2, cursor: 'pointer', '&:active': { bgcolor: 'grey.50' } }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 40, height: 40, bgcolor: rm.color + '20', color: rm.color, fontSize: 16, fontWeight: 700 }}>
                          {(u.fullName || u.email || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={700} noWrap>{u.fullName || u.username || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">{u.email}</Typography>
                        </Box>
                        <IconButton size="small" color="error"
                          onClick={e => { e.stopPropagation(); setConfirmDlg(u); }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box display="flex" gap={0.6} flexWrap="wrap" mt={1.2} alignItems="center">
                        <Chip label={rm.label} size="small" color={ROLE_COLOR[u.role] || 'default'} variant="outlined" sx={{ height: 22 }} />
                        {u.isSuperAdmin && <Chip label="Super Admin" size="small" color="error" sx={{ height: 22 }} />}
                        <Chip label={u.status} size="small" color={STATUS_COLOR[u.status] || 'default'} sx={{ height: 22 }} />
                        {u.userGroup && <Chip label={u.userGroup} size="small" variant="outlined" sx={{ height: 22 }} />}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {u.lastLogin ? `Last login ${fmtDate(u.lastLogin)}` : 'Never logged in'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
          <Box sx={{ overflowX: 'auto', mx: { xs: -1, sm: 0 } }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>User</strong></TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Username</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Verified</strong></TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Group</strong></TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Last Login</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      {search ? 'No users match your search.' : 'No users yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paged.map(u => {
                const rm = roleMeta(u.role);
                return (
                  <TableRow key={u._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}
                        onClick={() => openEdit(u)}
                        sx={{ cursor: 'pointer', '&:hover .user-name': { color: 'primary.main', textDecoration: 'underline' } }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: rm.color + '20', color: rm.color, fontSize: 14, fontWeight: 700 }}>
                          {(u.fullName || u.email || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography className="user-name" variant="body2" fontWeight={600} lineHeight={1.2}>{u.fullName || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{u.username || '—'}</TableCell>
                    <TableCell>
                      <Chip label={rm.label} size="small" color={ROLE_COLOR[u.role] || 'default'} variant="outlined" />
                      {u.isSuperAdmin && (
                        <Chip label="Super Admin" size="small" color="error" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={u.status} size="small" color={STATUS_COLOR[u.status] || 'default'} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {u.emailVerified
                        ? <Tooltip title="Email verified"><CheckCircle fontSize="small" sx={{ color: 'success.main' }} /></Tooltip>
                        : <Tooltip title="Awaiting email verification"><Cancel fontSize="small" sx={{ color: 'warning.main' }} /></Tooltip>}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {u.userGroup ? <Chip label={u.userGroup} size="small" variant="outlined" /> : <em style={{ color: '#bbb', fontSize: 12 }}>—</em>}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmtDate(u.lastLogin)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setConfirmDlg(u)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
          )}
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </Card>

      {/* Confirm Delete */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete user <strong>{confirmDlg?.email}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Dialog */}
      {discardDialog}
      <Dialog open={dlgOpen} onClose={attemptClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {(() => { const Ic = roleMeta(role).icon; return <Avatar sx={{ width: 36, height: 36, bgcolor: roleMeta(role).color + '20', color: roleMeta(role).color }}><Ic fontSize="small" /></Avatar>; })()}
          {editing ? `Edit User — ${editing.email}` : 'New User'}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}
              ref={el => el && el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}>
              {error}
            </Alert>
          )}

          {/* ── Section: Account ── */}
          <Typography variant="overline" color="text.secondary" fontWeight={700}>Account Details</Typography>
          <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="Full Name" name="fullName" value={form.fullName} onChange={handle} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email *" name="email" value={form.email} onChange={handle} fullWidth size="small" type="email"
                disabled={!!editing} helperText={editing ? 'Email cannot be changed' : ' '} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Username" name="username" value={form.username} onChange={handle} fullWidth size="small"
                helperText="Defaults to email prefix" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Role" name="role" value={form.role} onChange={handle} fullWidth size="small">
                {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Status" name="status" value={form.status} onChange={handle} fullWidth size="small">
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            {isSuperAdmin && (
              <Grid item xs={12} sm={8}>
                <TextField select label="School" name="schoolId" value={form.schoolId} onChange={handle} fullWidth size="small"
                  helperText="Assign or move this user to a school (super-admin only)">
                  <MenuItem value=""><em>— No school (awaiting assignment) —</em></MenuItem>
                  {schools.map(s => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.name} ({s.code}) — {s.provisionStatus}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>

          {viewerIsSuperAdmin && editing && (
            <>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Switch
                    checked={form.isSuperAdmin}
                    disabled={editing && (viewer.id === editing._id || viewer._id === editing._id) && form.isSuperAdmin}
                    onChange={e => setForm(f => ({ ...f, isSuperAdmin: e.target.checked }))}
                    color="error"
                  />
                }
                label={<Typography variant="body2"><strong>Super Admin</strong> — platform owner, sees and manages every school</Typography>}
              />
              {editing && (viewer.id === editing._id || viewer._id === editing._id) && form.isSuperAdmin && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5, mb: 1 }}>
                  You can't remove your own super-admin status.
                </Typography>
              )}
            </>
          )}

          {/* ── Section: Password ── */}
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="overline" color="text.secondary" fontWeight={700}>Password</Typography>

          {!editing ? (
            // CREATE — temporary password
            <Box sx={{ mt: 1, mb: 2 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={7}>
                  <TextField
                    label="Temporary Password *" name="password" value={form.password} onChange={handle}
                    fullWidth size="small" type={showPwd ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPwd(v => !v)} edge="end">
                            {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Button fullWidth variant="outlined" startIcon={<AutoFixHigh />} size="medium"
                    onClick={() => { setForm(f => ({ ...f, password: genPassword() })); setShowPwd(true); }}
                    sx={{ height: 40 }}>
                    Generate
                  </Button>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Share this temporary password securely. The account is automatically email-verified, and the user
                will be prompted to set their own password on first login.
              </Alert>
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={<Switch checked={form.mustChangePassword}
                  onChange={e => setForm(f => ({ ...f, mustChangePassword: e.target.checked }))} />}
                label="Force password change on first login"
              />
            </Box>
          ) : (
            // EDIT — reset by email (primary), manual override (optional)
            <Box sx={{ mt: 1, mb: 2 }}>
              <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
                <Button variant="contained" color="warning" startIcon={<MarkEmailRead />}
                  onClick={sendResetEmail} disabled={resetSending}>
                  {resetSending ? 'Sending…' : 'Send Password Reset Email'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Emails a reset code to the user so they set their own password.
                </Typography>
              </Box>

              <FormControlLabel
                sx={{ mt: 1.5, display: 'block' }}
                control={<Switch checked={manualPwd} onChange={e => { setManualPwd(e.target.checked); if (!e.target.checked) setForm(f => ({ ...f, password: '' })); else setForm(f => ({ ...f, mustChangePassword: true })); }} />}
                label={<Typography variant="body2"><strong>Admin reset</strong> — set a temporary password (user must change on next login)</Typography>}
              />
              {manualPwd && (
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      label="New Password" name="password" value={form.password} onChange={handle}
                      fullWidth size="small" type={showPwd ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPwd(v => !v)} edge="end">
                              {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Button fullWidth variant="outlined" startIcon={<AutoFixHigh />}
                      onClick={() => { setForm(f => ({ ...f, password: genPassword() })); setShowPwd(true); }}
                      sx={{ height: 40 }}>
                      Generate
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch checked={form.mustChangePassword}
                        onChange={e => setForm(f => ({ ...f, mustChangePassword: e.target.checked }))} />}
                      label="Force password change on next login"
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* ── Section: Access & Permissions (staff only) ── */}
          {showAccess && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Access &amp; Permissions</Typography>
              <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField select label="User Group" name="userGroup" value={form.userGroup} onChange={handle} fullWidth size="small"
                    helperText="Inherits the group's permission sets">
                    <MenuItem value=""><em>— None —</em></MenuItem>
                    {groups.map(g => <MenuItem key={g.code} value={g.code}>{g.code} — {g.description}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Direct Permission Sets</InputLabel>
                    <Select multiple value={form.permissionSets}
                      onChange={e => setForm(f => ({ ...f, permissionSets: e.target.value }))}
                      input={<OutlinedInput label="Direct Permission Sets" />}
                      renderValue={selected => (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {selected.map(v => <Chip key={v} label={v} size="small" />)}
                        </Box>
                      )}>
                      {pSets.map(ps => (
                        <MenuItem key={ps.code} value={ps.code}>
                          <strong>{ps.code}</strong>&nbsp;— {ps.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </>
          )}

          {role === 'admin' && (
            <Alert severity="info" icon={<AdminPanelSettings />} sx={{ mb: 2 }}>
              Administrators bypass all permission checks and have full access to every module.
              Assigning a group or permission sets here is optional — for organisational reference only.
            </Alert>
          )}

          {/* ── Section: Linked Records ── */}
          {(showEmpLink || showAdmLink) && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Linked Record</Typography>
              <Grid container spacing={2} sx={{ mt: 0, mb: 1 }}>
                {showEmpLink && (
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Employee Number" name="employeeNo" value={form.employeeNo} onChange={handle} fullWidth size="small"
                      helperText={role === 'classTeacher' ? 'Select the teacher\'s HR record' : 'Links login to employee record'}>
                      <MenuItem value=""><em>— Select an employee —</em></MenuItem>
                      {employees.map(emp => <MenuItem key={emp._id} value={emp.employeeNo}>{emp.employeeNo} — {`${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.jobTitle || '(no name)'}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}
                {showAdmLink && (
                  <Grid item xs={12} sm={6}>
                    <TextField label="Admission No (child)" name="admNo" value={form.admNo} onChange={handle}
                      fullWidth size="small" helperText="The learner this parent can view" />
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {role === 'parent' && (
            <Alert severity="info" icon={<FamilyRestroom />}>
              Parents only access the Parent Portal — their child's fee balance and academic performance.
              Link the admission number above.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;
